import React, { ReactElement } from 'react';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  ADD_MISCELLANEOUS_FILES_DIALOG,
  ADD_TO_PRODUCTION_GROUP_DIALOG,
  ARCHIVE_CONFIRMATION_DIALOG,
  CHOOSE_TRUSS_DESIGN_DRAWING_TEMPLATES_DIALOG,
  COMPONENT_COPY_CONFIRMATION_DIALOG,
  COMPONENT_MOVEMENT_CONFIRMATION_DIALOG,
  CREATE_PRODUCTION_GROUP_DIALOG,
  MANAGE_PRODUCTION_GROUPS_DIALOG,
  MATERIAL_DETAILS_OPTIONS_DIALOG,
  MOVE_COPY_DESTINATION_DIALOG,
  PROJECT_FILES_DIALOG,
  QUANTITIES_DIALOG,
  REPLACE_PRODUCTION_COMPONENTS,
  SHOP_DRAWING_OPTIONS_DIALOG,
  TRUSS_DESIGN_DRAWING_DOWNLOAD_DIALOG,
} from '../store/actions/dialogActions';
import { AddMiscellaneousFilesDialog } from './dialogs/AddMiscellaneousFilesDialog';
import { AddToProductionGroupDialog } from './dialogs/AddToProductionGroupDialog';
import { ArchiveConfirmationDialog } from './dialogs/ArchiveConfirmationDialog';
import ChooseQuantities from './dialogs/ChooseQuantitiesDialog';
import ChooseTrussDesignDrawingTemplatesDialog from './dialogs/ChooseTrussDesignDrawingTemplatesDialog';
import { ComponentCopyConfirmationDialog } from './dialogs/ComponentCopyConfirmationDialog';
import { ComponentMovementConfirmationDialog } from './dialogs/ComponentMovementConfirmationDialog';
import { CreateProductionGroupDialog } from './dialogs/CreateProductionGroupDialog';
import { ManageProductionGroupsDialog } from './dialogs/ManageProductionGroupsDialog';
import { MaterialDetailsOptionsDialog } from './dialogs/MaterialDetailsOptionsDialog';
import { MoveCopyDestinationDialog } from './dialogs/MoveCopyDestinationDialog';
import { ProjectFilesDialog } from './dialogs/ProjectFilesDialog';
import { ReplaceProductionComponentsDialog } from './dialogs/ReplaceProductionComponentsDialog';
import { ShopDrawingOptionsDialog } from './dialogs/ShopDrawingOptionsDialog';
import TrussDesignDrawingDownloadDialog from './dialogs/TrussDesignDrawingDownloadDialog';
import { useAppSelector } from './hooks';

/** Renders whichever project-wide dialog is currently requested in the popup slice. */
export function GlobalDialogs() {
  const popup = useAppSelector((state) => state.popup);

  if (!popup.popupName) return null;

  let dialog: ReactElement | null = null;
  switch (popup.popupName) {
    case COMPONENT_MOVEMENT_CONFIRMATION_DIALOG:
      dialog = <ComponentMovementConfirmationDialog requestedMovement={popup.options} />;
      break;

    case COMPONENT_COPY_CONFIRMATION_DIALOG:
      dialog = <ComponentCopyConfirmationDialog {...popup.options} />;
      break;

    case QUANTITIES_DIALOG:
      dialog = <ChooseQuantities {...popup.options} />;
      break;

    case MATERIAL_DETAILS_OPTIONS_DIALOG:
      dialog = <MaterialDetailsOptionsDialog {...popup.options} />;
      break;

    case SHOP_DRAWING_OPTIONS_DIALOG:
      dialog = <ShopDrawingOptionsDialog {...popup.options} />;
      break;

    case ARCHIVE_CONFIRMATION_DIALOG:
      dialog = <ArchiveConfirmationDialog {...popup.options} />;
      break;

    case PROJECT_FILES_DIALOG:
      dialog = <ProjectFilesDialog {...popup.options} />;
      break;

    case MOVE_COPY_DESTINATION_DIALOG:
      dialog = <MoveCopyDestinationDialog {...popup.options} />;
      break;

    case CHOOSE_TRUSS_DESIGN_DRAWING_TEMPLATES_DIALOG:
      dialog = <ChooseTrussDesignDrawingTemplatesDialog {...popup.options} />;
      break;

    case TRUSS_DESIGN_DRAWING_DOWNLOAD_DIALOG:
      dialog = <TrussDesignDrawingDownloadDialog {...popup.options} />;
      break;

    case ADD_MISCELLANEOUS_FILES_DIALOG:
      dialog = <AddMiscellaneousFilesDialog jobId={popup.options.jobId} reviewUrl={popup.options.reviewUrl} />;
      break;

    case CREATE_PRODUCTION_GROUP_DIALOG:
      dialog = <CreateProductionGroupDialog {...popup.options} />;
      break;

    case ADD_TO_PRODUCTION_GROUP_DIALOG:
      dialog = <AddToProductionGroupDialog {...popup.options} />;
      break;

    case MANAGE_PRODUCTION_GROUPS_DIALOG:
      dialog = <ManageProductionGroupsDialog {...popup.options} />;
      break;

    case REPLACE_PRODUCTION_COMPONENTS:
      dialog = <ReplaceProductionComponentsDialog {...popup.options} />;
      break;

    default:
      assertExhaustiveSwitch(popup);
  }

  return dialog;
}
